import { TournamentCompetition } from "@mixitone/models";
import MixitoneController from "@mixitone/mvc";
import { withStateToggle } from "@mixitone/util";

interface State {
  loading: boolean;
  editing: boolean;
  editingDescription: boolean;
  competition: TournamentCompetition;
}

interface Props {
  competitionId: string;
}

export class CompetitionDrawerController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      loading: true,
      editing: false,
      editingDescription: false,
      competition: new TournamentCompetition({}),
    };
  }

  override async initialize(props: Props) {
    await this.changeProps(props);
  }

  override async changeProps(newProps: Props) {
    if (newProps.competitionId !== this.state.competition.id) {
      await this.loadCompetition(newProps.competitionId);
    }
  }

  @withStateToggle("loading")
  private async loadCompetition(id: string) {
    const competition = await TournamentCompetition.query()
      .eq("id", id)
      .preloadCounts("registrations", "entries")
      .first();

    if (!competition) {
      throw new Error(`Competition ${id} not found`);
    }

    this.state.competition = competition;

    const subscription = await competition.subscribe((updated) => {
      this.state.competition = updated;
    });
    this.addDependency("competition", subscription);
  }

  get registeredPlayerCount() {
    if (!this.state.competition.registrations.loaded) return 0;

    return this.state.competition.registrations?.count() ?? 0;
  }

  get entryCount() {
    if (!this.state.competition.entries.loaded) return 0;

    return this.state.competition.entries?.count() ?? 0;
  }

  actionEditCompetition(editing: boolean) {
    this.state.editing = editing;
  }

  actionEditDescription(editingDescription: boolean) {
    this.state.editingDescription = editingDescription;
  }

  async actionEnableSignUp(enable: boolean) {
    if (this.state.competition.status === "setup" && enable) {
      this.state.competition.status = "entry_open";
    }
    if (this.state.competition.status === "entry_open" && !enable) {
      this.state.competition.status = "setup";
    }
    await this.state.competition.save();
  }

  async actionSaveDescription() {
    await this.state.competition.save();
    this.state.editingDescription = false;
  }

  async actionCancelDescription() {
    await this.state.competition.reload();
    this.state.editingDescription = false;
  }

  actionRegenerateGroups() {
    if (this.state.competition.groupsLocked) return;
    // Dispatch event that TournamentController will listen for
    window.dispatchEvent(
      new CustomEvent("regenerateGroups", {
        detail: { competitionId: this.state.competition.id! },
      }),
    );
  }

  actionSetGroupsLocked(locked: boolean) {
    window.dispatchEvent(
      new CustomEvent("setGroupsLocked", {
        detail: { competitionId: this.state.competition.id!, locked },
      }),
    );
  }

  actionResetPlayoffDivisions() {
    window.dispatchEvent(
      new CustomEvent("resetPlayoffDivisions", {
        detail: { competitionId: this.state.competition.id! },
      }),
    );
  }
}
